import * as React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { ICON_MAP } from 'src/utils/constants';
import { useListDiscountPolicies } from 'src/features/discountPolicies/api/useListDiscountPolicies';
import { useListVoucherTypes } from 'src/features/voucherTypes/api/useListVoucherTypes';
import { billingSimulationMessages } from 'src/features/billingSimulation/utils/messages';
import { BillingSimulationFormSchemaValues } from 'src/features/billingSimulation/utils/formSchema';

type BillingSimulationFormProps = {
    form: UseFormReturn<BillingSimulationFormSchemaValues>;
    onSubmit: (values: BillingSimulationFormSchemaValues) => void;
    isPending: boolean;
};

const BillingSimulationForm = ({ form, onSubmit, isPending }: BillingSimulationFormProps) => {
    const { formatMessage } = useIntl();

    const [searchDiscountPolicy, setSearchDiscountPolicy] = React.useState<string>('');
    const [searchVoucherType, setSearchVoucherType] = React.useState<string>('');

    const startDate = form.watch('start_date');

    const { data: discountPolicies } = useListDiscountPolicies({ search: searchDiscountPolicy });
    const { data: voucherTypes } = useListVoucherTypes({ search: searchVoucherType, is_active: true });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(billingSimulationMessages.startDate)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime dateISO={field.value} onChange={field.onChange} disableTime />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(billingSimulationMessages.endDate)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disableTime
                                    disabledDate={startDate ? { before: new Date(startDate) } : undefined}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SearchSelectFormField
                name="discount_policy_ids"
                control={form.control}
                label={formatMessage(billingSimulationMessages.discountPolicies)}
                items={discountPolicies?.results ?? []}
                isMultiSelect
                clearable
                required={false}
                placeholder={formatMessage(billingSimulationMessages.discountPoliciesPlaceholder)}
                searchPlaceholder={formatMessage(billingSimulationMessages.discountPoliciesSearchPlaceholder)}
                emptyTitle={formatMessage(billingSimulationMessages.noDiscountPolicies)}
                emptyDescription={formatMessage(billingSimulationMessages.noDiscountPoliciesFound)}
                getItemValue={(policy) => policy.id}
                getItemLabel={(policy) => policy.name}
                getItemKeywords={(policy) => [policy.name]}
                loadOptions={setSearchDiscountPolicy}
            />
            <SearchSelectFormField
                name="voucher_type_ids"
                control={form.control}
                label={formatMessage(billingSimulationMessages.voucherTypes)}
                items={voucherTypes?.results ?? []}
                isMultiSelect
                clearable
                required={false}
                placeholder={formatMessage(billingSimulationMessages.voucherTypesPlaceholder)}
                searchPlaceholder={formatMessage(billingSimulationMessages.voucherTypesSearchPlaceholder)}
                emptyTitle={formatMessage(billingSimulationMessages.noVoucherTypes)}
                emptyDescription={formatMessage(billingSimulationMessages.noVoucherTypesFound)}
                getItemValue={(voucherType) => voucherType.id}
                getItemLabel={(voucherType) => voucherType.name}
                getItemKeywords={(voucherType) => [voucherType.name, voucherType.code]}
                loadOptions={setSearchVoucherType}
            />
            <Button type="submit" disabled={isPending} className="mt-2 self-start">
                {isPending ? (
                    <>
                        <FontAwesomeIcon icon={ICON_MAP.SPINNER} spin />
                        {formatMessage(billingSimulationMessages.runningSimulation)}
                    </>
                ) : (
                    <>
                        <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                        {formatMessage(billingSimulationMessages.runSimulation)}
                    </>
                )}
            </Button>
        </Form>
    );
};

export default BillingSimulationForm;
